<template>
  <div id="broker-status" class="bst-page">
    <!-- Barre d'outils -->
    <div class="bst-toolbar">
      <div class="bst-toolbar-group">
        <vs-button type="border" size="small" icon-pack="feather" icon="icon-arrow-left" :to="{ name: 'broker-check' }">
          Broker Check
        </vs-button>
        <vs-button size="small" icon-pack="feather" icon="icon-refresh-cw" :disabled="loading" @click="load">
          Refresh
        </vs-button>
      </div>
      <div v-if="!loading && !loadError && rows.length" class="bst-toolbar-group bst-count">
        <span class="bst-pill" :class="readOnlyCount ? 'pill-warning' : 'pill-muted'">{{ readOnlyCount }} read-only</span>
        <span class="bst-muted">{{ accountCount }} account(s) · {{ rows.length }} broker(s)</span>
      </div>
    </div>
    <p class="bst-hint">
      <feather-icon icon="InfoIcon" svgClasses="h-4 w-4" />
      A read-only broker can still sign in and view their quotations, invoices and contacts; every create, update, send or delete is refused by the server. Changes take effect immediately.
    </p>

    <div class="bst-content">
      <div v-if="loading" class="bst-empty">
        <feather-icon icon="LoaderIcon" svgClasses="h-6 w-6" class="bst-spin" />
        <div>Loading broker accounts…</div>
      </div>
      <vs-alert v-else-if="loadError" color="danger" active="true" class="mb-4">{{ loadError }}</vs-alert>
      <div v-else-if="!rows.length" class="bst-empty">
        <feather-icon icon="InboxIcon" svgClasses="h-8 w-8" />
        <div>No broker found (no contact flagged as broker).</div>
      </div>

      <vx-card v-else class="bst-card" title="Broker accounts">
        <div class="overflow-x-auto">
          <table class="bst-table">
            <thead>
              <tr>
                <th>Broker</th>
                <th>Account</th>
                <th>Active</th>
                <th>Online</th>
                <th>Read-only</th>
                <th>Last change</th>
                <th>History</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="r in rows">
                <tr :key="rowKey(r)" :class="{ 'bst-off': r.read_only }">
                  <td class="strong">
                    {{ r.broker_name }}
                    <span v-if="!r.person_active" class="bst-pill pill-muted ml-1">inactive contact</span>
                    <div v-if="r.person_email" class="bst-sub">{{ r.person_email }}</div>
                  </td>
                  <td>
                    <template v-if="r.iduser">
                      {{ r.username }}
                      <span v-if="isAdminRow(r)" class="bst-pill pill-warning ml-1"
                        title="This account also has the admin role: the read-only flag still applies everywhere.">admin</span>
                      <div v-if="r.user_email" class="bst-sub">{{ r.user_email }}</div>
                    </template>
                    <span v-else class="bst-pill pill-muted">No ERP account</span>
                  </td>
                  <td>
                    <template v-if="r.iduser">
                      <span v-if="r.user_active" class="bst-pill pill-success">Active</span>
                      <span v-else class="bst-pill pill-danger">Inactive</span>
                    </template>
                    <span v-else class="bst-muted">—</span>
                  </td>
                  <td>
                    <span v-if="r.online" class="bst-pill pill-info">online</span>
                    <span v-else class="bst-muted">—</span>
                  </td>
                  <td>
                    <div v-if="r.iduser" class="bst-switch">
                      <vs-switch color="warning" :value="!!r.read_only"
                        :disabled="!!busy[r.iduser] || r.iduser === me" @input="toggle(r, $event)" />
                      <span v-if="r.iduser === me" class="bst-muted">(you)</span>
                      <span v-else-if="r.read_only" class="bst-pill pill-warning">read-only</span>
                      <span v-else class="bst-muted">full access</span>
                    </div>
                    <span v-else class="bst-muted">—</span>
                  </td>
                  <td>
                    <template v-if="r.read_only_at">
                      <span v-if="r.read_only">Read-only since {{ formatDate(r.read_only_at) }}</span>
                      <span v-else>Re-enabled {{ formatDate(r.read_only_at) }}</span>
                      <div v-if="r.read_only_by" class="bst-sub">by {{ r.read_only_by }}</div>
                    </template>
                    <span v-else class="bst-muted">—</span>
                  </td>
                  <td>
                    <vs-button v-if="r.iduser" type="flat" size="small" icon-pack="feather" icon="icon-list"
                      @click="toggleHistory(r)">{{ auditFor === r.iduser ? 'Hide' : 'History' }}</vs-button>
                  </td>
                </tr>
                <tr v-if="r.iduser && auditFor === r.iduser" :key="rowKey(r) + '-audit'" class="bst-audit">
                  <td colspan="7">
                    <audit-log-table entity="user" :id="r.iduser" />
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
// Broker Check -> Broker Status : switch « lecture seule » par compte broker
// (users.read_only, mig 089). L'API est reservee aux admins (brokerstatus.routes.js) ;
// le serveur applique le drapeau partout (roleGate, 403 read_only_user).
import axios from '@/axios.js'
import moment from 'moment'
import AuditLogTable from '@/components/AuditLogTable.vue'

function errorMessage (err, fallback) {
  const data = err && err.response && err.response.data
  return (data && data.message) || fallback
}

export default {
  name: 'BrokerStatus',
  components: { AuditLogTable },
  data () {
    return {
      loading: true,
      loadError: null,
      rows: [],
      me: Number(localStorage.getItem('login-id')) || null,
      busy: {}, // iduser -> true pendant l'appel
      auditFor: null
    }
  },
  computed: {
    accountCount () { return this.rows.filter(r => r.iduser).length },
    readOnlyCount () { return this.rows.filter(r => r.iduser && r.read_only).length }
  },
  methods: {
    rowKey (r) { return r.iduser ? 'u' + r.iduser : 'p' + r.idperson },
    isAdminRow (r) {
      return !!(r.roles && String(r.roles).split(',').map(s => s.trim()).includes('admin'))
    },
    formatDate (v) { return v ? moment(v).format('DD/MM/YYYY HH:mm') : '' },
    async load () {
      this.loading = true
      this.loadError = null
      try {
        const res = await axios.get('/brokerstatus')
        this.rows = res.data.brokers || []
        if (res.data.me) this.me = Number(res.data.me)
      } catch (err) {
        this.loadError = errorMessage(err, 'Could not load the broker accounts.')
      } finally {
        this.loading = false
      }
    },
    async toggle (row, value) {
      const on = !!value
      if (!row.iduser || on === !!row.read_only) return
      this.$set(this.busy, row.iduser, true)
      try {
        const res = await axios.put('/brokerstatus/' + row.iduser, { read_only: on })
        Object.assign(row, res.data)
        this.notify('success', on ? 'Read-only enabled' : 'Read-only lifted',
          on
            ? `${row.broker_name} can now only view their quotations, invoices and contacts.`
            : `${row.broker_name} has full access again.`)
      } catch (err) {
        this.notify('danger', 'Denied', errorMessage(err, 'Could not update the broker status.'))
        // Le switch est pilote par la ligne : on force le re-rendu sur l'ancienne valeur.
        this.$set(row, 'read_only', !!row.read_only)
      } finally {
        this.$set(this.busy, row.iduser, false)
      }
    },
    toggleHistory (r) { this.auditFor = this.auditFor === r.iduser ? null : r.iduser },
    notify (color, title, text) {
      this.$vs.notify({ title, text, color, iconPack: 'feather', icon: color === 'success' ? 'icon-check' : 'icon-alert-circle', position: 'bottom-right', time: 8000 })
    }
  },
  created () {
    if (!(localStorage.getItem('login-roles') || '').toLowerCase().includes('admin')) {
      this.notify('danger', 'Denied', 'Redirection')
      this.$router.push({ path: '/' })
      return
    }
    this.load()
  }
}
</script>

<style scoped>
/* Meme habillage que Broker Check -> Paiement (BrokerCommissionPayment.vue). */
.bst-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.06);
}
.bst-toolbar-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
}
.bst-count {
  font-size: 0.8125rem;
}
.bst-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 4px 18px;
  color: #64748b;
  font-size: 0.8125rem;
}
.bst-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 3rem 1rem;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  text-align: center;
}
.bst-spin {
  animation: bst-spin 1s linear infinite;
}
@keyframes bst-spin {
  to {
    transform: rotate(360deg);
  }
}
.bst-muted {
  color: #94a3b8;
}
.bst-sub {
  color: #94a3b8;
  font-weight: 400;
  font-size: 0.6875rem;
}
.bst-pill {
  display: inline-block;
  padding: 2px 8px;
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}
.pill-warning {
  background: #fff3e0;
  color: #e65100;
}
.pill-success {
  background: #e8f5e9;
  color: #2e7d32;
}
.pill-danger {
  background: #ffebee;
  color: #c62828;
}
.pill-info {
  background: #e3f2fd;
  color: #1565c0;
}
.pill-muted {
  background: #f1f5f9;
  color: #64748b;
}
.bst-card .overflow-x-auto {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
.bst-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
  white-space: nowrap;
}
.bst-table th {
  padding: 8px 8px;
  background: #455a64;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.2;
  text-align: left;
  vertical-align: bottom;
}
.bst-table td {
  padding: 8px 8px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
  vertical-align: middle;
}
.bst-table tbody tr:nth-child(even) td {
  background: #f8fafc;
}
.bst-table tbody tr:hover td {
  background: #f1f5f9;
}
.bst-table .strong {
  font-weight: 700;
}
.bst-table tr.bst-off td {
  background: #fff8e1;
}
.bst-table tr.bst-audit td {
  white-space: normal;
  background: #fff;
}
.bst-switch {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
